import { useState } from 'react';
import { Alert, Button, Modal, Table } from 'react-bootstrap';
import { ROLLOUT_STATE, isRolloutState } from '@prerender/feature-flags';

import {
  FeatureFlag,
  FeatureFlagState,
  FlagSide,
  RELEASE_FLAG_STATES,
  describeFlagState,
  removesFeatureFromHolders,
} from './FeatureFlag';
import { useListFeatureFlagsQuery, useUpdateFeatureFlagMutation } from './featureFlagsApi';
import FeatureFlagRow from './FeatureFlagRow';
import BulkGrantModal from './BulkGrantModal';
import { serverMessageOf } from './serverMessageOf';

const FLAG_TABLE = { maxWidth: '48rem' };

const STATE_MEANINGS: Record<FeatureFlagState, string> = {
  off: 'nobody, staff included',
  internal: 'Prerender staff only (@prerender.io accounts), ignoring grants',
  granted: "only accounts that hold the flag, through their own features or their plan's",
  on: 'everybody',
  rollout: 'a fixed share of accounts, chosen by account id, plus staff and grant holders',
};

type FlagPick = {
  readonly state: FeatureFlagState;
  readonly percent: string;
};

type FlagChange = {
  readonly name: string;
  readonly from: FlagSide;
  readonly to: FlagSide;
};

const PLAIN_DIGITS = /^\d+$/;

const parseRolloutPercent = (text: string): number | undefined => {
  const percent = PLAIN_DIGITS.test(text) ? Number(text) : undefined;
  return isRolloutState({ state: ROLLOUT_STATE, percent }) ? percent : undefined;
};

const sameSide = (one: FlagSide, other: FlagSide): boolean =>
  one.state === other.state && one.percent === other.percent;

const sideOf = (flagPick: FlagPick): FlagSide => ({
  state: flagPick.state,
  percent: flagPick.state === ROLLOUT_STATE ? parseRolloutPercent(flagPick.percent) : undefined,
});

const changesBetween = (flags: FeatureFlag[], picks: Record<string, FlagPick>): FlagChange[] =>
  flags
    .filter((flag) => picks[flag.name] !== undefined)
    .map((flag) => ({
      name: flag.name,
      from: { state: flag.state, percent: flag.percent },
      to: sideOf(picks[flag.name]),
    }))
    .filter((change) => !sameSide(change.from, change.to));

const storedPercentText = (flag: FeatureFlag): string =>
  flag.state === ROLLOUT_STATE && flag.percent !== undefined ? String(flag.percent) : '';

const describeSide = (side: FlagSide): string => describeFlagState(side.state, side.percent);

const lacksValidPercent = (flagPick: FlagPick): boolean =>
  flagPick.state === ROLLOUT_STATE && parseRolloutPercent(flagPick.percent) === undefined;

const holdsInvalidPercent = (flagPick: FlagPick | undefined): boolean =>
  flagPick !== undefined && flagPick.percent !== '' && lacksValidPercent(flagPick);

const withoutKey = <T,>(record: Record<string, T>, key: string): Record<string, T> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

function FeatureFlagsList() {
  const { data } = useListFeatureFlagsQuery();
  const [updateFeatureFlag] = useUpdateFeatureFlagMutation();
  const [picks, setPicks] = useState<Record<string, FlagPick>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSummary, setSaveSummary] = useState<string | undefined>(undefined);
  const [grantingFlag, setGrantingFlag] = useState<string | undefined>(undefined);

  const flags = data?.flags ?? [];
  const holdsFlags = data?.configured && flags.length > 0;
  const holdsNoFlags = data?.configured && flags.length === 0;
  const changes = changesBetween(flags, picks);
  const saveBlocked = saving || changes.length === 0 || Object.values(picks).some(lacksValidPercent);

  const pickState = (flag: FeatureFlag, state: FeatureFlagState): void => {
    setPicks((current) =>
      state === flag.state ? withoutKey(current, flag.name) : { ...current, [flag.name]: { state, percent: '' } }
    );
    setSaveErrors((current) => withoutKey(current, flag.name));
    setSaveSummary(undefined);
  };

  const changePercent = (flag: FeatureFlag, text: string): void => {
    setPicks((current) => ({
      ...current,
      [flag.name]: { state: current[flag.name]?.state ?? flag.state, percent: text },
    }));
  };

  const discardChanges = (): void => {
    setPicks({});
    setSaveErrors({});
    setSaveSummary(undefined);
  };

  const saveChanges = async (): Promise<void> => {
    setConfirming(false);
    setSaving(true);
    const refused: Record<string, string> = {};
    const saved: Record<string, FlagSide> = {};
    for (const change of changes) {
      const result = await updateFeatureFlag({ name: change.name, state: change.to.state, percent: change.to.percent });
      if ('error' in result) {
        refused[change.name] = serverMessageOf(result.error) ?? `Failed to update ${change.name}`;
      } else {
        saved[change.name] = change.to;
      }
    }
    setSaving(false);
    setPicks((current) =>
      Object.fromEntries(
        Object.entries(current).filter(
          ([name, flagPick]) => saved[name] === undefined || !sameSide(saved[name], sideOf(flagPick))
        )
      )
    );
    setSaveErrors(refused);
    const refusedCount = Object.keys(refused).length;
    setSaveSummary(
      refusedCount > 0 ? `Saved ${changes.length - refusedCount} of ${changes.length} changes.` : undefined
    );
  };

  return (
    <main>
      <div className="mb-4">
        <h1>Feature flags</h1>
        {holdsFlags && <p>Changes take effect within about 30 seconds.</p>}
        {holdsFlags && (
          <ul aria-label="What each state means" className="list-unstyled small text-muted mb-0">
            {RELEASE_FLAG_STATES.map((state) => (
              <li key={state}>
                <strong>{state}</strong> — {STATE_MEANINGS[state]}
              </li>
            ))}
          </ul>
        )}
      </div>

      {data?.configured === false && (
        <Alert variant="warning">
          The feature switch table is not set up: the <code>feature_switches</code> config row is missing, and this page
          cannot create it.
        </Alert>
      )}

      {holdsNoFlags && <p>No feature flags are registered. Flags are added through migrations, not on this page.</p>}

      {holdsFlags && (
        <>
          <Table striped size="sm" className="align-middle" style={FLAG_TABLE}>
            <tbody>
              {flags.map((flag) => (
                <FeatureFlagRow
                  key={flag.name}
                  flag={flag}
                  shownState={picks[flag.name]?.state ?? flag.state}
                  unsaved={changes.some((change) => change.name === flag.name)}
                  percentText={picks[flag.name]?.percent ?? storedPercentText(flag)}
                  percentInvalid={holdsInvalidPercent(picks[flag.name])}
                  saveError={saveErrors[flag.name]}
                  onPick={(state) => pickState(flag, state)}
                  onPercentChange={(text) => changePercent(flag, text)}
                  onGrant={() => setGrantingFlag(flag.name)}
                />
              ))}
            </tbody>
          </Table>

          {saveSummary && <Alert variant="warning">{saveSummary}</Alert>}

          <div className="d-flex gap-2 mb-4">
            <Button variant="primary" disabled={saveBlocked} onClick={() => setConfirming(true)}>
              Save
            </Button>
            <Button variant="outline-secondary" disabled={saving || changes.length === 0} onClick={discardChanges}>
              Discard
            </Button>
          </div>

          <Modal show={confirming} onHide={() => setConfirming(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Save feature flag changes?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <ul className="mb-0">
                {changes.map((change) => (
                  <li key={change.name}>{`Set ${change.name} from ${describeSide(change.from)} to ${describeSide(change.to)}`}</li>
                ))}
              </ul>
              {changes
                .filter((change) => removesFeatureFromHolders(change.from, change.to))
                .map((change) => (
                  <Alert key={change.name} variant="warning" className="mt-3 mb-0">
                    {`Some accounts that hold ${change.name} will lose it.`}
                  </Alert>
                ))}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveChanges}>
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>

          {grantingFlag && <BulkGrantModal flag={grantingFlag} onClose={() => setGrantingFlag(undefined)} />}
        </>
      )}
    </main>
  );
}

export default FeatureFlagsList;
